import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Accordion, Badge, Box, Callout, Code, Em, MdxComponents, Separator, Text } from '@workos-inc/design-system';
import get from 'lodash.get';
import { type MDXRemoteProps, MDXRemote } from 'next-mdx-remote';
import * as React from 'react';
import { formatReferenceValuePath, isReferencedValue } from 'src/utils/reference-helpers';
import { ReferenceDescriptions, ReferenceEntity } from 'src/utils/reference-schema';
import { Enum } from './enum';
import { Link } from './link';
import { useReferenceDescriptions } from './reference-data-provider';
import { TypeDefinitionContext, TypeDefinitionSyntax, useTypeDefinition } from './type-definition-context';
import { getSyntax } from './type-definition-entity';
import { TypeDefinitionPair } from './type-definition-pair';
import { TypeDefinitionToken } from './type-definition-token';
interface ReferenceAccordionProps {
  entities?: ReferenceEntity[];
  syntax?: TypeDefinitionSyntax;
}
export const ReferenceAccordion = ({
  entities = [],
  syntax = 'object'
}: ReferenceAccordionProps) => {
  const {
    language
  } = useTypeDefinition();
  const descriptions = useReferenceDescriptions();

  // Recursively expand the nested accordions if needed
  const defaultValue = entities.filter(entity => hasExpandedProperties(entity)).map(entity => entity.key);
  return <Accordion.Root defaultValue={defaultValue} size="1" data-sentry-element="Accordion.Root" data-sentry-component="ReferenceAccordion" data-sentry-source-file="reference-accordion.tsx">
      {entities.map(entity => <Accordion.Item className={entity.deprecated ? 'ReferenceDeprecated' : undefined} key={entity.key} value={entity.key}>
          <Accordion.Header as="h5">
            <code className="ReferenceDefinitionCode">
              <TypeDefinitionContext.Provider key={entity.key} value={{
            entity,
            language,
            syntax
          }}>
                {entity.type ? <TypeDefinitionPair /> : <TypeDefinitionToken token={entity.key} />}
              </TypeDefinitionContext.Provider>
            </code>
            {entity.deprecated && <Badge color="yellow" ml="2" size="1">
                Deprecated
              </Badge>}
          </Accordion.Header>

          {/* Disable `hiddenUntilFound` because DOM size is a performance issue on the reference page */}
          <Accordion.Content hiddenUntilFound={false}>
            {entity.description && <Box mb="-3">
                <MDXRemote
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          components={mdxComponents as MDXRemoteProps['components']} frontmatter={mdxRemoteFrontmatter} scope={mdxRemoteScope} compiledSource={getDescriptionValue(entity.description, descriptions)} />
              </Box>}

            {entity.properties && <ReferenceAccordion entities={entity.properties} syntax={getSyntax(entity)} />}
          </Accordion.Content>
        </Accordion.Item>)}
    </Accordion.Root>;
};

// Don't pass empty object to MDX Remote directly.
// MDX Remote must receive a stable object, otherwise performance on large pages suffers.
const mdxRemoteFrontmatter = {};
const mdxRemoteScope = {};
const hasExpandedProperties = (entity: ReferenceEntity): boolean => entity.expanded || entity.properties?.some(hasExpandedProperties) || false;
const getDescriptionValue = (value: string, descriptions: ReferenceDescriptions) => {
  let descriptionValue = value;
  if (isReferencedValue(value)) {
    const descriptionsKeyPath = formatReferenceValuePath(value);
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    descriptionValue = get(descriptions, descriptionsKeyPath) as string;
    if (!descriptionValue) {
      throw new Error(`Missing description: ${descriptionsKeyPath}`);
    }
  }
  return descriptionValue;
};
const mdxComponents: MdxComponents = {
  a: Link,
  code: props => <Code highContrast color="gray" {...props} />,
  em: Em,
  hr: props => <Separator decorative my="5" size="4" {...props} />,
  li: props => <Box mb="1">
      <li>
        <Text size="2" {...props} />
      </li>
    </Box>,
  ol: props => <Box mb="3" pl="3">
      <ol style={{
      margin: 0,
      padding: 0
    }} {...props} />
    </Box>,
  ul: props => <Box mb="3" pl="3">
      <ul style={{
      margin: 0,
      padding: 0
    }} {...props} />
    </Box>,
  p: props => <Text as="p" mb="3" size="2" {...props} />,
  Enum,
  Callout: ({
    children
  }: {
    children?: React.ReactNode;
  }) => <Callout.Root color="gray" my="3">
      <Callout.Icon className="ReferenceCalloutIcon">
        <InfoCircledIcon height="16" width="16" />
      </Callout.Icon>

      <Callout.Text>{children}</Callout.Text>
    </Callout.Root>
};